import useSWR, { SWRConfiguration, SWRResponse } from "swr";
import useSWRInfinite, {
  SWRInfiniteConfiguration,
  SWRInfiniteResponse,
  SWRInfiniteKeyLoader,
} from "swr/infinite";
import { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { createFetcher, TApiType } from "../../utils/fetcher";

export type TGetRequest = AxiosRequestConfig | null;

interface SWRReturn<Data, Error>
  extends SWRResponse<AxiosResponse<Data>["data"], AxiosError<Error>> {}

export interface SWRConfig<Data = unknown, Error = unknown>
  extends SWRConfiguration<AxiosResponse<Data>["data"], AxiosError<Error>> {}

export interface IRequestError extends AxiosError {}

export default function useRequest<Data = unknown, Error = unknown>(
  request: TGetRequest,
  config: SWRConfig<Data, Error> = {},
  authed: boolean = true,
  api: TApiType = "quine",
  shouldCamelizeKeys: boolean = true
): SWRReturn<Data, Error> {
  return useSWR<AxiosResponse<Data>["data"], AxiosError<Error>>(
    request && JSON.stringify(request),
    () =>
      createFetcher({ authed, api, shouldCamelizeKeys })
        .request<Data>(request!)
        .then(({ data }) => data),
    config
  );
}

interface SWRInfiniteReturn<Data, Error>
  extends SWRInfiniteResponse<AxiosResponse<Data>["data"], AxiosError<Error>> {}

export interface SWRInfiniteConfig<Data = unknown, Error = unknown>
  extends SWRInfiniteConfiguration<
    AxiosResponse<Data>["data"],
    AxiosError<Error>
  > {}

export function useInfiniteRequest<Data = unknown, Error = unknown>(
  getKey: SWRInfiniteKeyLoader,
  config: SWRInfiniteConfig<Data, Error> = {},
  authed: boolean = true,
  api: TApiType = "quine",
  shouldCamelizeKeys: boolean = true
): SWRInfiniteReturn<Data, Error> {
  return useSWRInfinite<AxiosResponse<Data>["data"], AxiosError<Error>>(
    getKey,
    (stringifiedRequest: string) =>
      createFetcher({ authed, api, shouldCamelizeKeys })
        .request<Data>(JSON.parse(stringifiedRequest))
        .then(({ data }) => data),
    config
  );
}
